<template>
    <div class="ah-page variable-statement-page" @click="onPageClick">
        <div class="page-header">
            <div class="ah-container">
                <div class="title-row">
                    <div class="title">
                        <h1>財務收支</h1>
                        <el-select v-model="yearSelect" class="title-dropdown">
                            <el-option v-for="item in yearList" :key="item.key" :value="item.value" :label="item.label"></el-option>
                        </el-select>
                    </div>
                    <div class="actions">
                        <el-input
                            v-model="queryParams.MOO_TPC"
                            class="search"
                            placeholder="單號、摘要關鍵字"
                            prefix-icon="el-icon-search"
                            :clearable="true"
                            style="width: 250px; margin-right: 10px"
                            @keyup.enter.native="onSimpleSearchKeyUp"
                            @clear="updateData(true)"
                        ></el-input>
                        <el-button type="dark-transparent" round @click="isShowAdvSearch = isShowAdvSearch ? false : true" icon="el-icon-zoom-in"></el-button>
                        <el-button
                            v-if="isAuth('E01')"
                            type="dark"
                            @click="
                                $router.push({
                                    name: 'variable-statement-bank-transfer'
                                })
                            "
                            >存款帳戶互轉</el-button
                        >
                        <el-button
                            v-if="isAuth('E01')"
                            type="success"
                            @click="
                                onEditDialogVisibleChange({
                                    visible: true,
                                    purpose: 'income'
                                })
                            "
                            >新增一筆財務收入</el-button
                        >
                        <el-button
                            v-if="isAuth('E01')"
                            type="danger"
                            @click="
                                onEditDialogVisibleChange({
                                    visible: true,
                                    purpose: 'payout'
                                })
                            "
                            >新增一筆財務支出</el-button
                        >
                    </div>
                </div>
            </div>
        </div>
        <div class="row main-color">
            <div class="ah-container">
                <transition enter-active-class="animate__animated animate__fadeIn">
                    <div class="ah-card adv-search-card" v-if="isShowAdvSearch">
                        <div class="title">進階搜尋</div>
                        <div class="content">
                            <el-form ref="form" :model="queryParams" label-width="220px" label-position="right" size="mini">
                                <el-form-item label="日期">
                                    <el-date-picker
                                        v-model="queryParams.queryDate"
                                        type="daterange"
                                        placeholder="選擇日期"
                                        :clearable="false"
                                        value-format="timestamp"
                                        start-placeholder="開始日期"
                                        end-placeholder="結束日期"
                                        :default-time="['00:00:00', '23:59:59']"
                                        :picker-options="pickerOptions"
                                    >
                                    </el-date-picker>
                                </el-form-item>
                                <el-form-item label="關鍵字">
                                    <el-input v-model="queryParams.MOO_TPC" placeholder="單號、摘要關鍵字" style="max-width: 400px" />
                                </el-form-item>
                                <el-form-item prop="amt" label="金額">
                                    <MoneyInput v-model="queryParams.MinAmt" style="width: 130px" size="small" is-search />
                                    <span
                                        style="
                                            display: inline-block;
                                            width: 40px;
                                            text-align: center;
                                        "
                                        >~</span
                                    >
                                    <MoneyInput v-model="queryParams.MaxAmt" style="width: 130px" size="small" is-search />
                                </el-form-item>
                                <el-form-item prop="itemSnos" label="收支項目">
                                    <ahSelectVariableStateItem
                                        ref="ahSelectVariableStateItem"
                                        v-model="queryParams.itemSnos"
                                        :enableType="['payout', 'cIn', 'cOut', 'cInElse', 'cOutElse']"
                                        :system-only-list="['payoutSettings']"
                                        :showTip="false"
                                        show-system-default
                                        multiple
                                    />
                                </el-form-item>
                                <el-form-item prop="cny" label="幣別">
                                    <ahElSelectCny v-model="queryParams.cnyList" multiple />
                                </el-form-item>
                                <el-form-item prop="bank" label="銀行帳戶">
                                    <el-select v-model="queryParams.bankSnos" multiple>
                                        <el-option v-for="item in bankAccountOptions" :key="item.sno" :value="item.sno" :label="item.tpc"></el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" class="primary-action" @click="handleAdvSearchClick">
                                        查詢
                                    </el-button>
                                    <el-button @click="handleAdvSearchClearClick(false)">
                                        清除
                                    </el-button>
                                    <el-button @click="handleAdvSearchClearClick(true)">
                                        清空並返回
                                    </el-button>
                                </el-form-item>
                            </el-form>
                        </div>
                    </div>
                </transition>
            </div>
            <div class="ah-container" id="variable-statement-page-statis">
                <el-row :gutter="20">
                    <el-col :sm="6">
                        <div class="ah-card main-color">
                            <div class="content">
                                <div class="statis-title">財務總收入</div>
                                <div class="statis-num">
                                    <Money :v="Math.round(totalInAmount)" />
                                    <span class="cny">TWD</span>
                                </div>
                                <div v-if="isShowCnys" class="cny-statistic statis-num">
                                    <div v-for="(item, i) in amoutTotalStatisticalList" :key="`unpaid-${i}`" class="cny-statistic-block">
                                        <template v-if="item.incomeAmount">
                                            <Money :v="item.incomeAmount" />
                                            <span class="cny">{{ item.cny }}</span>
                                            <span v-if="item.cny !== 'TWD'" class="cny"> 約當台幣 <Money :v="item.incomeAmountTwd" round/></span>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-col>
                    <el-col :sm="6">
                        <div class="ah-card main-color">
                            <div class="content">
                                <div class="statis-title">財務總支出</div>
                                <div class="statis-num">
                                    <Money :v="Math.round(totalOutAmount)" />
                                    <span class="cny">TWD</span>
                                </div>
                                <div v-if="isShowCnys" class="cny-statistic statis-num">
                                    <div v-for="(item, i) in amoutTotalStatisticalList" :key="`unpaid-${i}`" class="cny-statistic-block">
                                        <template v-if="item.costingAmount">
                                            <Money :v="item.costingAmount" />
                                            <span class="cny">{{ item.cny }}</span>
                                            <span v-if="item.cny !== 'TWD'" class="cny"> 約當台幣 <Money :v="item.costingAmountTwd" round/></span>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-col>
                    <el-col :sm="12">
                        <div class="ah-card main-color">
                            <div class="content">
                                <div class="statis-title">淨額 <ahTip>財務總收入 - 財務總支出</ahTip></div>
                                <div class="statis-num">
                                    <Money :v="Math.round(totalInAmount - totalOutAmount)" />
                                    <span class="cny">TWD</span>
                                </div>
                                <div v-if="isShowCnys" class="cny-statistic statis-num">
                                    <div v-for="(item, i) in amoutTotalStatisticalList" :key="`unpaid-${i}`" class="cny-statistic-block">
                                        <template v-if="item.incomeAmount - item.costingAmount">
                                            <Money :v="item.incomeAmount - item.costingAmount" />
                                            <span class="cny">{{ item.cny }}</span>
                                            <span v-if="item.cny !== 'TWD'" class="cny"> 約當台幣 <Money :v="item.incomeAmountTwd - item.costingAmountTwd" round/></span>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </el-col>
                </el-row>
                <div class="page-statis-actions">
                    <el-button size="small" type="primary" @click="onShowCnysClick">
                        {{ isShowCnys ? "隱藏外幣" : "顯示所有幣別" }}
                    </el-button>
                </div>
            </div>
        </div>
        <div class="ah-container" style="margin-top: 25px">
            <div class="ah-panel">
                <div class="scrollview">
                    <div class="tabs-bar">
                        <div class="tabs-list">
                            <div
                                :class="{
                                    item: true,
                                    active: queryParams.TabNo === 1
                                }"
                                @click="handleTabChange(1)"
                                @keydown="() => {}"
                            >
                                <span class="name">全部</span>
                                <span class="count">{{ tabCounts.allCount }}</span>
                                <span class="total"> <Money :v="tabCounts.incomeSubtotal - tabCounts.costingSubtotal"/></span>
                            </div>
                            <div
                                :class="{
                                    item: true,
                                    active: queryParams.TabNo === 2
                                }"
                                @click="handleTabChange(2)"
                                @keydown="() => {}"
                            >
                                <span class="name">收入</span>
                                <span class="count">{{ tabCounts.incomeCount }}</span>
                                <span class="total"> <Money :v="tabCounts.incomeSubtotal"/></span>
                            </div>
                            <div
                                :class="{
                                    item: true,
                                    active: queryParams.TabNo === 3
                                }"
                                @click="handleTabChange(3)"
                                @keydown="() => {}"
                            >
                                <span class="name">支出</span>
                                <span class="count">{{ tabCounts.costingCount }}</span>
                                <span class="total"> <Money :v="tabCounts.costingSubtotal"/></span>
                            </div>
                            <div
                                :class="{
                                    item: true,
                                    active: queryParams.TabNo === 4
                                }"
                                @click="handleTabChange(4)"
                                @keydown="() => {}"
                            >
                                <span class="name">待收</span>
                                <span class="count">{{ tabCounts.notReceivePaymentCount }}</span>
                                <span class="total"> <Money :v="tabCounts.notReceivePaymentSubtotal"/></span>
                            </div>
                            <div
                                :class="{
                                    item: true,
                                    active: queryParams.TabNo === 5
                                }"
                                @click="handleTabChange(5)"
                                @keydown="() => {}"
                            >
                                <span class="name">待繳</span>
                                <span class="count">{{ tabCounts.notPaymentCount }}</span>
                                <span class="total"> <Money :v="tabCounts.notPaymentSubtotal"/></span>
                            </div>
                        </div>
                        <div>
                            <el-button size="mini" @click="onExport">匯出 Excel</el-button>
                        </div>
                    </div>
                </div>
                <div class="panel-body">
                    <el-table
                        v-loading="status == 'loading'"
                        :data="datas"
                        style="width: 100%"
                        class="ah-el-data-table row-click"
                        :row-class-name="getRowClass"
                        @row-click="handleDataRowClick"
                        @sort-change="onSortChange"
                        empty-text="沒有資料"
                    >
                        <el-table-column prop="moo" label="編號" width="120">
                            <template slot-scope="scope">
                                <span class="idx">{{ scope.row.moo }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="類型" width="130">
                            <template slot-scope="scope">
                                <div v-if="scope.row.type">
                                    <span v-if="scope.row.flg === -1" class="ah-status payout">
                                        {{ getTypeNameByNum(scope.row.type) }}
                                    </span>
                                    <span v-if="scope.row.flg === 1" class="ah-status income">
                                        {{ getTypeNameByNum(scope.row.type) }}
                                    </span>
                                </div>
                                <div v-else>
                                    <span v-if="scope.row.flg === -1" class="ah-status payout"> 支出</span>
                                    <span v-if="scope.row.flg === 1" class="ah-status income">收入</span>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column prop="date" label="繳費/收款日期" width="150" sortable="custom">
                            <template slot-scope="scope">
                                <span>{{ scope.row.paY_DAT | dateFormater("YYYY-MM-dd") }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="tpc" label="摘要" min-width="200">
                            <template slot-scope="scope">
                                <div class="strong">{{ scope.row.tpc }}</div>
                                <span style="color: #888; font-size: 9pt">
                                    {{ getItemName(scope.row.iteM_SNO) }}
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column label="建立日期" sortable width="120" v-if="false">
                            <template slot-scope="scope">
                                <span>{{ scope.row.crT_DAT | dateFormater("YYYY-MM-dd") }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="amt" label="金額" width="100" align="right">
                            <template slot-scope="scope">
                                <Money :v="scope.row.amt" :precision="scope.row.cny === 'TWD' ? null : 4" />
                            </template>
                        </el-table-column>
                        <el-table-column prop="cny" label="幣別" width="80"></el-table-column>
                        <el-table-column label="狀態" width="90">
                            <template slot-scope="scope">
                                <div v-if="scope.row.flg === -1">
                                    <el-tag type="success" size="small" v-if="+new Date() > scope.row.paY_DAT"> <i class="el-icon-check"></i> 已繳 </el-tag>
                                    <el-tag type="warning" size="small" v-else> <i class="el-icon-time"></i> 待繳 </el-tag>
                                </div>
                                <div v-else>
                                    <el-tag type="success" size="small" v-if="+new Date() > scope.row.paY_DAT"> <i class="el-icon-check"></i> 已收 </el-tag>
                                    <el-tag type="warning" size="small" v-else> <i class="el-icon-time"></i> 待收 </el-tag>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>
                    <el-pagination
                        layout="prev, pager, next"
                        :page-size.sync="queryParams.PageSize"
                        :total.sync="totalDataCount"
                        :current-page.sync="queryParams.PageNum"
                        :small="isMobile()"
                        @current-change="handlePaginationChange"
                        class="pagination"
                    >
                    </el-pagination>
                </div>
            </div>
        </div>
        <DetailDialog ref="detailDialog" :sno="detailDialogSno" :visible="detailDialogVisible" @edit="onEditDialogVisibleChange" @close="handleDetailDialogClose" />
        <EditDialog ref="editDialog" :sno="editDialog.sno" :visible="editDialog.visible" :purpose="editDialog.purpose" @refresh="onEditDialogRefresh" @close="onEditDialogVisibleChange" />
    </div>
</template>
<script>
import moment from 'moment';
import { datePickerOption, statementTypes } from '@/assets/commonData/options';
import _ from 'lodash';
import ahElSelectCny from '@/components/ui/ahElSelectCny.vue';
import ahSelectVariableStateItem from '@/components/ui/ahSelectVariableStateItem.vue';
import DetailDialog from './DetailDialog.vue';
import EditDialog from './EditDialog.vue';
import mixins from './ExportMixins';

export default {
    name: 'VariableStatement',
    mixins: [mixins],
    components: {
        DetailDialog,
        EditDialog,
        ahElSelectCny,
        ahSelectVariableStateItem,
    },
    data() {
        return {
            detailDialogSno: null,
            editDialog: { sno: null, visible: false, purpose: null },
            detailDialogVisible: false,
            lastDetailDialogSno: null,
            isShowAdvSearch: false,
            queryParams: {
                MOO_TPC: null,
                TabNo: 1,
                date: null,
                SDT: null,
                EDT: null,
                FLG: null,
                itemSnos: [],
                BNK_SNO: null,
                MinAmt: null,
                MaxAmt: null,
                CNY: null,
                PageNum: 1,
                PageSize: 25,
                SortColumnNo: null,
                OrderBy: 1,
                queryDate: null,
            },
            amoutTotalStatisticalList: [],
            prevQuery: {},
            tabCounts: {
                allCount: 0,
                incomeCount: 0,
                costingCount: 0,
                notReceivePaymentCount: 0,
                notPaymentCount: 0,
            },
            searchDatePreset: 'month',
            totalDataCount: 0,
            totalInAmount: 0,
            totalOutAmount: 0,
            datePerid: null,
            year: null,
            status: '',
            datas: null,
            errorMsg: '',
            inList: [],
            outList: [],
            bankAccountList: [],

            yearList: [],
            pickerOptions: datePickerOption,
            isShowCnys: false,
        };
    },
    computed: {
        FlagFDatas() {
            return [
                { value: null, name: '不限類型' },
                { value: 1, name: '收入' },
                { value: -1, name: '支出' },
            ];
        },
        yearSelect: {
            set(val) {
                if (!val) {
                    this.queryParams.queryDate = null;
                    this.updateData(true);
                    return;
                }

                const sd = Number(
                    moment()
                        .year(val)
                        .startOf('year')
                        .format('x'),
                );
                const ed = Number(
                    moment()
                        .year(val)
                        .endOf('year')
                        .format('x'),
                );

                this.queryParams.queryDate = [sd, ed];
                this.updateData(true);
            },
            get() {
                if (!this.queryParams.queryDate) return '所有年份';
                const year = moment(Number(this.queryParams.queryDate[0])).year();
                const daysOfYear = year % 4 === 0 ? 366 : 365;
                const sd = moment(Number(this.queryParams.queryDate[0]));
                const ed = moment(Number(this.queryParams.queryDate[1]));

                // eslint-disable-next-line eqeqeq
                if (sd.format('DDD') == 1 && ed.format('DDD') == daysOfYear) {
                    return year;
                }
                return '自訂';
            },
        },
        bankAccountOptions() {
            return this.$store.getters['bankAccount/bankAccountOptions'];
        },
    },
    created() {
        this.fetchBankList();
    },
    mounted() {
        this.initYearSelect();
        this.updateData();
    },
    methods: {
        initYearSelect() {
            const START = 2020;
            const END = moment().year() + 1;

            this.yearList = [];
            this.yearList.push({ label: '所有年份', value: null });
            for (let i = END; i >= START; i -= 1) {
                this.yearList.push({ label: i, value: i, key: `year-list-${i}-${+new Date()}` });
            }

            const sd = Number(
                moment()
                    .startOf('year')
                    .format('x'),
            );
            const ed = Number(
                moment()
                    .endOf('year')
                    .format('x'),
            );
            this.queryParams.queryDate = [sd, ed];
        },
        onSimpleSearchKeyUp() {
            if (!this.isShowAdvSearch) this.queryParams.queryDate = null;
            this.updateData(true);
        },
        resetQueryParams() {
            this.queryParams = {
                MOO_TPC: null,
                TabNo: 1,
                date: null,
                SDT: null,
                EDT: null,
                FLG: null,
                itemSnos: [],
                bankSnos: null,
                MinAmt: null,
                MaxAmt: null,
                cnyList: null,
                PageNum: 1,
                PageSize: 25,
                SortColumnNo: null,
                OrderBy: 1,
                queryDate: null,
            };
            this.$refs.ahSelectVariableStateItem.clear();
        },
        handlePaginationChange() {
            this.updateData();
        },
        handleAdvSearchClick() {
            this.updateData(true);
            setTimeout(() => {
                this.scrollToId('variable-statement-page-statis', 'start');
            }, 300);
        },
        getTypeNameByNum(type) {
            if (type === 1) return '營業其他收入';
            if (type === 2) return '營業其他支出';
            if (type === 5) return '財務資產收入';
            if (type === 6) return '財務資產支出';
            return null;
        },
        handleAdvSearchClearClick(resumeDefault) {
            this.resetQueryParams();

            if (resumeDefault) {
                this.initYearSelect();
                this.updateData(true);
                this.isShowAdvSearch = false;
            }
        },

        // 按下表格一行
        handleDataRowClick(data) {
            this.detailDialogSno = data.sno;
            this.detailDialogVisible = true;
            this.lastDetailDialogSno = null;
        },

        // 關閉單筆細節視窗
        handleDetailDialogClose(refresh) {
            this.lastDetailDialogSno = this.detailDialogSno;
            this.detailDialogSno = null;
            this.detailDialogVisible = false;

            if (refresh) this.updateData();
        },

        onEditDialogVisibleChange({ visible, purpose, sno = null }) {
            if (!visible) {
                this.editDialog.purpose = null;
                this.editDialog.sno = null;
            }
            this.editDialog.visible = visible;
            this.editDialog.purpose = purpose;
            this.editDialog.sno = sno;
        },
        onEditDialogRefresh() {
            this.updateData();
            this.$refs.detailDialog.refresh();
        },

        // 取得銀行列表（用於進階搜尋下拉單）
        fetchBankList() {
            this.$api({
                url: 'Tool/GetBankAccountOptions',
                method: 'GET',
            }).then((resp) => {
                this.status = 'success';
                this.bankAccountList = resp.data.options;
            });
        },
        async updateData(resetPageNum = false) {
            this.status = 'loading';

            if (resetPageNum) this.queryParams.PageNum = 1;

            if (this.queryParams.queryDate != null) {
                this.queryParams.SDT = Number(
                    moment(_.first(this.queryParams.queryDate))
                        .startOf('day')
                        .format('x'),
                );
                this.queryParams.EDT = Number(
                    moment(_.last(this.queryParams.queryDate))
                        .endOf('day')
                        .format('x'),
                );
            } else {
                this.queryParams.SDT = null;
                this.queryParams.EDT = null;
            }

            this.prevQuery = { ...this.queryParams };

            const { query, resetQuery } = this.getManualQuery(this.queryParams);
            const resp = await this.$api({
                url: `VariableStatementSettingPage/GetList${query}`,
                params: { ...this.queryParams, ...resetQuery },
                method: 'GET',
            });

            console.log('VariableStatement', resp);

            if (this.queryParams.PageNum !== 1 && !resp.data.totalNum) {
                this.queryParams.PageNum -= 1;
                this.updateData();
                return;
            }
            this.status = 'success';
            this.datas = resp.data.items;

            // 統計數字
            this.totalDataCount = resp.data.totalNum;
            this.totalInAmount = resp.data.statisticsInfo.incomeAmount;
            this.totalOutAmount = resp.data.statisticsInfo.costingAmount;
            this.amoutTotalStatisticalList = resp.data.statisticsInfo.statisticalList;

            // 狀態分頁筆數
            this.tabCounts.allCount = resp.data.statisticsInfo.allCount;
            this.tabCounts.incomeCount = resp.data.statisticsInfo.incomeCount;
            this.tabCounts.costingCount = resp.data.statisticsInfo.costingCount;
            this.tabCounts.notReceivePaymentCount = resp.data.statisticsInfo.notReceivePaymentCount;
            this.tabCounts.notPaymentCount = resp.data.statisticsInfo.notPaymentCount;

            this.tabCounts.allSubtotal = Math.round(resp.data.statisticsInfo.allSubtotal);
            this.tabCounts.incomeSubtotal = Math.round(resp.data.statisticsInfo.incomeSubtotal);
            this.tabCounts.costingSubtotal = Math.round(resp.data.statisticsInfo.costingSubtotal);
            this.tabCounts.notReceivePaymentSubtotal = Math.round(resp.data.statisticsInfo.notReceivePaymentSubtotal);
            this.tabCounts.notPaymentSubtotal = Math.round(resp.data.statisticsInfo.notPaymentSubtotal);

            // router replace
            /*
                    var params = {}
                    for (var key in this.conditions) {
                        if (this.conditions[key])
                            params[key] = this.conditions[key]
                    }
                    this.$router.replace({ name: 'variable-statement', params })
                    */
        },

        //
        handleTabChange(TabNo) {
            this.queryParams.TabNo = TabNo;
            this.updateData(true);
        },

        onSortChange(event) {
            console.log(event);
            if (event.prop !== 'date') return;

            if (!event.order) {
                this.queryParams.SortColumnNo = null;
                this.updateData();
                return;
            }

            this.queryParams.SortColumnNo = 2;
            if (event.order === 'ascending') this.queryParams.OrderBy = 1;
            else this.queryParams.OrderBy = 2;
            this.updateData();
        },
        getRowClass({ row }) {
            if (row.sno === this.detailDialogSno) {
                return 'curr-detail-dialog-target';
            }
            if (row.sno === this.lastDetailDialogSno) {
                return 'last-detail-dialog-target';
            }
            return '';
        },
        onPageClick(event) {
            if (event.target.classList.contains('ah-page') || event.target.classList.contains('ah-container')) {
                this.lastDetailDialogSno = null;
            }
        },

        getManualQuery(query) {
            const returnValue = [..._.map(query.bankSnos, (o) => `bankSnos=${o}`), ..._.map(query.itemSnos, (o) => `itemSnos=${o}`), ..._.map(query.cnyList, (o) => `cnyList=${o}`)];
            return {
                query: returnValue.length ? `?${returnValue.join('&')}` : '',
                resetQuery: {
                    bankSnos: undefined,
                    itemSnos: undefined,
                    cnyList: undefined,
                    queryDate: undefined,
                },
            };
        },
        getCnyRat(cny) {
            const cnyOption = this.$store.getters['cny/cnyOptions'].find((x) => x.cny === cny);
            if (cnyOption) return cnyOption.rat;
            return 1;
        },
        getItemName(itemSno) {
            const hierarchy = [null, null, null];

            for (const t in statementTypes) {
                const options = this.$store.getters['statement/statementUiOptions'][statementTypes[t].key];
                for (const i in options) {
                    const lv1Item = options[i];
                    for (const j in lv1Item.childSettings) {
                        const lv2Item = lv1Item.childSettings[j];
                        if (lv2Item.sno === itemSno) {
                            hierarchy[0] = statementTypes[t].value;
                            hierarchy[1] = lv1Item.iteM_NAM;
                            hierarchy[2] = lv2Item.iteM_NAM;
                            break;
                        }
                    }
                }
            }

            return hierarchy.join(' / ');
        },
        onShowCnysClick() {
            this.isShowCnys = !this.isShowCnys;
        },
    },
};
</script>
<style lang="scss">
.ah-page.variable-statement-page {
    .ah-panel {
        > .scrollview {
            width: 100%;
            overflow-x: auto;
        }

        .tabs-bar {
            display: flex;
            justify-content: space-between;
            min-width: 900px;
            align-items: center;
            padding-top: 0;
            background-color: #fff;
            padding: 0 20px;
            border-bottom: 1px solid #eee;
            border-top-left-radius: 12px;
            border-top-right-radius: 12px;
        }
    }

    .tabs-list {
        display: flex;
        gap: 10px;

        >.item {
            border: 1px solid #f5f5f5;
            background-color: #f5f5f5;
            padding: 5px 12px;
            margin: 10px 0;
            border-radius: 12px;
            font-size: 10pt;
            color: #333;
            cursor: pointer;

            &:hover {
                background-color: #f6f6f6;
                border: 1px solid #ddd;
            }

            &.active {
                color: #463cb2;
                background-color: #fff;
                border: 1px solid #9289ed;
            }

            >.name {
                font-weight: bold;
                margin: 0 5px;
            }
            >.count {
                font-weight: bold;
                margin-right: 12px;
                &::before {
                    content: "(";
                }
                &::after {
                    content: ")";
                }
            }
            .total {
                >.money {
                    font-weight: normal;
                    opacity: 0.8;
                }
            }
        }
    }

    .cny-hint {
        position: relative;
        top: -10px;
        font-size: 10pt;
        color: #fff;
        opacity: 0.7;
    }
}
</style>
